<template>
  <!-- Loading State -->
  <div v-if="loading" class="p-8 flex flex-col items-center justify-center">
    <div class="relative">
      <div class="w-12 h-12 rounded-full border-2 border-gray-200 dark:border-gray-700"></div>
      <div class="w-12 h-12 rounded-full border-2 border-red-500 border-t-transparent animate-spin absolute top-0"></div>
    </div>
    <p class="text-xs text-gray-500 dark:text-gray-400 mt-3">Yükleniyor...</p>
  </div>

  <!-- Empty State -->
  <div v-else-if="!orders.length" class="p-8 flex flex-col items-center justify-center">
    <div class="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
      <i class="fas fa-inbox text-gray-400 text-2xl"></i>
    </div>
    <p class="text-sm text-gray-500 dark:text-gray-400">Canlı atama bekleyen sipariş yok</p>
    <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Yeni siparişler geldiğinde burada görünecek</p>
  </div>

  <!-- Orders List -->
  <div v-else>
    <!-- Status Notifications -->
    <div v-if="ordersWithPendingVehicles.length > 0 || ordersWithRejectedVehicles.length > 0" class="px-4 py-2 space-y-2 border-b border-gray-100 dark:border-gray-800">
      <!-- Pending Vehicles -->
      <div v-if="ordersWithPendingVehicles.length > 0" class="flex items-center justify-between px-3 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
        <div class="flex items-center gap-2">
          <div class="w-6 h-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            <i class="fas fa-clock text-amber-600 dark:text-amber-400 text-[10px]"></i>
          </div>
          <span class="text-xs text-amber-700 dark:text-amber-400 font-medium">
            {{ totalPendingVehicles }} araç onay bekliyor
          </span>
        </div>
      </div>

      <!-- Rejected Vehicles -->
      <!-- <div v-if="ordersWithRejectedVehicles.length > 0" class="flex items-center justify-between px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <div class="flex items-center gap-2">
          <div class="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <i class="fas fa-times text-red-600 dark:text-red-400 text-[10px]"></i>
          </div>
          <span class="text-xs text-red-700 dark:text-red-400 font-medium">
            {{ totalRejectedVehicles }} araç reddetti
          </span>
        </div>
        <button @click="handleReassignRejected" class="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors">
          Yeniden Ata
        </button>
      </div> -->
    </div>

    <!-- Crisis Orders Section -->
    <div v-if="crisisOrders.length > 0" class="border-b border-gray-100 dark:border-gray-800">
      <div class="px-4 py-2.5 bg-red-50/50 dark:bg-red-900/10 flex items-center justify-between sticky top-0 z-10">
        <div class="flex items-center gap-2">
          <span class="relative flex h-2 w-2">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span class="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
          </span>
          <span class="text-xs font-semibold text-red-700 dark:text-red-400">
            Kritik Siparişler ({{ crisisOrders.length }})
          </span>
        </div>
        <button @click.stop="toggleAll('crisis')" class="px-2 py-1 text-[10px] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-800/50 rounded transition-colors">
          <i :class="crisisExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="mr-1"></i>
          {{ crisisExpanded ? 'Kapat' : 'Aç' }}
        </button>
      </div>

      <div class="px-4 py-2 space-y-2  overflow-y-auto " style="max-height: calc(100vh - 400px);">
        <live-assignment-order-item
          v-for="order in crisisOrders"
          :key="`crisis-${order.id}`"
          :order="order"
          :active-tab="order.activeTab"
          :is-loading-nearby="order.isLoadingNearby"
          :is-loading-recommended="order.isLoadingRecommended"
          :ref="`orderItem-${order.id}`"
          @tab-change="(tab) => onTabChange(order.id, tab)"
          @assign="(vehicleId) => onAssignVehicle(order.id, vehicleId)"
          @bulkAssign="(vehicles) => $emit('bulkAssign',order.id, vehicles)"
        />
      </div>
    </div>

    <!-- Ready to Assign Orders -->
    <div v-if="normalOrdersWithVehicles.length > 0" class="border-b border-gray-100 dark:border-gray-800">
      <div class="px-4 py-2.5 bg-green-50/50 dark:bg-green-900/10 flex items-center justify-between sticky top-0 z-10">
        <div class="flex items-center gap-2">
          <div class="w-2 h-2 bg-green-500 rounded-full"></div>
          <span class="text-xs font-semibold text-green-700 dark:text-green-400">
            Atamaya Hazır ({{ normalOrdersWithVehicles.length }})
          </span>
        </div>
        <button @click.stop="toggleAll('normal')" class="px-2 py-1 text-[10px] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-800/50 rounded transition-colors">
          <i :class="normalExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="mr-1"></i>
          {{ normalExpanded ? 'Kapat' : 'Aç' }}
        </button>
      </div>

      <div class="px-4 py-2 space-y-2  overflow-y-auto " style="max-height: calc(100vh - 400px);">
        <live-assignment-order-item
          v-for="order in normalOrdersWithVehicles"
          :key="order.id"
          :order="order"
          :active-tab="order.activeTab"
          :is-loading-nearby="order.isLoadingNearby"
          :is-loading-recommended="order.isLoadingRecommended"
          :ref="`orderItem-${order.id}`"
          @tab-change="(tab) => onTabChange(order.id, tab)"
          @assign="(vehicleId) => onAssignVehicle(order.id, vehicleId)"
          @bulkAssign="(vehicles) => $emit('bulkAssign',order.id, vehicles)"
        />
      </div>
    </div>

    <!-- Orders Without Vehicles -->
    <div v-if="ordersWithoutVehicles.length > 0">
      <div class="px-4 py-2.5 bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-between sticky top-0 z-10">
        <div class="flex items-center gap-2">
          <div class="w-2 h-2 bg-gray-400 rounded-full"></div>
          <span class="text-xs font-semibold text-gray-600 dark:text-gray-400">
            Araç Bulunamadı ({{ ordersWithoutVehicles.length }})
          </span>
        </div>
      </div>

      <div class="px-4 py-2 space-y-2">
        <div v-for="order in ordersWithoutVehicles" :key="order.id"
             class="px-3 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="text-xs font-medium text-gray-700 dark:text-gray-300">
                {{ order.orderNo }}
              </div>
              <div class="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                {{ order.orderName }}
              </div>
              <div class="flex items-center gap-1 mt-1">
                <i class="fas fa-route text-[9px] text-gray-400"></i>
                <span class="text-[10px] text-gray-500 dark:text-gray-400">
                  {{ order.receivedName }} → {{ order.deliveredName }}
                </span>
              </div>
            </div>
            <div class="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
              Araç yok
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LiveAssignmentOrderItem from "./LiveAssignmentOrderItem.vue";

export default {
  name: "LiveAssignmentOrderList",
  components: {
    LiveAssignmentOrderItem,
  },
  props: {
    orders: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scrollPosition: 0,
      isExpanding: false,
      crisisExpanded: true,
      normalExpanded: true
    };
  },

  computed: {
    // Kriz durumundaki siparişler
    crisisOrders() {
      return this.orders.filter(order => order.hasCrisis === true);
    },

    // Normal siparişler (kriz durumunda olmayanlar)
    normalOrders() {
      return this.orders.filter(order => order.hasCrisis !== true);
    },

    // Araç önerisi olan normal siparişler
    normalOrdersWithVehicles() {
      return this.normalOrders.filter(order => order.hasAvailableVehicles === true);
    },

    // Araç önerisi olmayan siparişler
    ordersWithoutVehicles() {
      return this.orders.filter(order => order.hasAvailableVehicles === false);
    },

    // Onay bekleyen araçları olan siparişler
    ordersWithPendingVehicles() {
      return this.orders.filter(order => order.pendingVehicleCount > 0);
    },

    // Reddedilen araçları olan siparişler
    ordersWithRejectedVehicles() {
      return this.orders.filter(order => order.rejectedVehicleCount > 0);
    },

    // Toplam onay bekleyen araç sayısı
    totalPendingVehicles() {
      return this.ordersWithPendingVehicles.reduce((sum, order) => sum + (order.pendingVehicleCount || 0), 0);
    },

    // Toplam reddedilen araç sayısı
    totalRejectedVehicles() {
      return this.ordersWithRejectedVehicles.reduce((sum, order) => sum + (order.rejectedVehicleCount || 0), 0);
    },
  },

  methods: {
    onTabChange(orderId, tab) {
      this.$emit("tab-change", orderId, tab);
    },

    onAssignVehicle(orderId, vehicleId) {
      this.$emit("assign", orderId, vehicleId);
    },

    toggleAll(section) {
      let orders, isExpanded;

      if (section === 'crisis') {
        orders = this.crisisOrders;
        isExpanded = this.crisisExpanded;
        this.crisisExpanded = !this.crisisExpanded;
      } else {
        orders = this.normalOrdersWithVehicles;
        isExpanded = this.normalExpanded;
        this.normalExpanded = !this.normalExpanded;
      }

      orders.forEach(order => {
        const ref = this.$refs[`orderItem-${order.id}`];
        if (ref && ref[0]) {
          if (isExpanded && ref[0].expand) {
            ref[0].expand();
          } else if (!isExpanded && ref[0].collapse) {
            ref[0].collapse();
          }
        }
      });
    },

    handleReassignRejected() {
      console.log("Reddedilen araçlar yeniden atanacak");
    },
  },
};
</script>

<style scoped>
/* Custom animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-in {
  animation: fadeIn 0.2s ease-out;
}
</style>